import type { GrafanaApi } from '@backstage-community/plugin-grafana';
import type { Config } from '@backstage/config';

// Grafana org resolution for deep links. The plugin builds links as
// `host.domain + path` with no orgId, so Grafana resolves /d/<uid> in the
// viewer's current org and a viewer whose session sits in another org gets
// "Dashboard not found". orgId is read from grafana.hosts[].orgId.

export function orgIdForHost(
  config: Config,
  hostId: string | undefined,
): number | undefined {
  const hosts = config.getOptionalConfigArray('grafana.hosts') ?? [];
  const wanted =
    hostId ?? config.getOptionalString('grafana.defaultHost') ?? undefined;
  const host =
    wanted !== undefined
      ? hosts.find(h => h.getString('id') === wanted)
      : hosts[0];
  return host?.getOptionalNumber('orgId');
}

function withOrg(url: string, orgId: number | undefined): string {
  if (orgId === undefined || !url) return url;
  return `${url}${url.includes('?') ? '&' : '?'}orgId=${orgId}`;
}

export function withOrgId(api: GrafanaApi, config: Config): GrafanaApi {
  return {
    isUnifiedAlerting: (hostId?: string) => api.isUnifiedAlerting(hostId),
    listDashboards: async (query, hostId) => {
      const orgId = orgIdForHost(config, hostId);
      const dashboards = await api.listDashboards(query, hostId);
      return dashboards.map(d => ({
        ...d,
        url: withOrg(d.url, orgId),
        folderUrl: withOrg(d.folderUrl, orgId),
      }));
    },
    alertsForSelector: async (selectors, hostId) => {
      const orgId = orgIdForHost(config, hostId);
      const alerts = await api.alertsForSelector(selectors, hostId);
      return alerts.map(a => ({ ...a, url: withOrg(a.url, orgId) }));
    },
  };
}
